import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CityBackdrop } from '../../components/games/CityBackdrop';
import { MapIcon, Dice5Icon, SkullIcon, SparklesIcon, BuildingIcon } from 'lucide-react';
export function TtlBoardGamePage() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const formData = new FormData();
      formData.append('fields[email]', email);
      formData.append('ml-submit', '1');
      formData.append('anticsrf', 'true');
      await fetch('https://assets.mailerlite.com/jsonp/2211088/forms/182587044822779312/subscribe', {
        method: 'POST',
        body: formData,
        mode: 'no-cors'
      });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };
  const features = [{
    icon: <BuildingIcon className="w-6 h-6" data-id="element-954" />,
    title: 'Animated City Environments',
    desc: 'Travel through immersive neon-lit streets.'
  }, {
    icon: <MapIcon className="w-6 h-6" data-id="element-955" />,
    title: 'Interactive Event Systems',
    desc: 'Face unpredictable life events and scenarios.'
  }, {
    icon: <Dice5Icon className="w-6 h-6" data-id="element-956" />,
    title: 'Branching Life Paths',
    desc: 'Your choices, risks, and survival instincts determine what comes next.'
  }, {
    icon: <SkullIcon className="w-6 h-6" data-id="element-957" />,
    title: 'First-Person Chaos Realm',
    desc: 'Survive dangerous Chaos Realm moments.'
  }];
  return <div className="min-h-screen bg-slate-950 text-slate-200 pt-28 pb-24 overflow-hidden relative" data-id="element-958">
      {/* Background */}
      <div className="absolute inset-0 z-0 opacity-40" data-id="element-959">
        <CityBackdrop data-id="element-960" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/90 to-slate-950 z-0" data-id="element-961" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-962">
        {/* Hero Section */}
        <div className="text-center mb-16 pt-10" data-id="element-963">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="relative mx-auto mb-8 max-w-2xl" data-id="element-964">
            {/* Soft neon glow behind logo */}
            <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-600/30 via-purple-600/30 to-cyan-500/30 blur-3xl scale-90 -z-10" data-id="element-965" />
            <img src="https://cdn.magicpatterns.com/uploads/jDykrsQ1i5aZ95QGt41Tnf/ChatGPT_Image_May_12,_2026,_07_18_22_PM.png" alt="TTL: The Board Game" className="w-full h-auto object-contain drop-shadow-[0_0_30px_rgba(168,85,247,0.4)]" data-id="element-966" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-950/50 border border-cyan-800/50 text-sm font-bold text-cyan-400 mb-8 shadow-[0_0_15px_rgba(34,211,238,0.2)] uppercase tracking-widest" data-id="element-967">
            <SparklesIcon className="w-4 h-4" data-id="element-968" />
            Coming Soon
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="text-xl text-cyan-100/80 font-medium max-w-2xl mx-auto leading-relaxed" data-id="element-969">
            A cinematic life-simulation experience where the city itself becomes
            the game.
          </motion.p>
        </div>

        {/* Description */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-slate-900/60 backdrop-blur-md rounded-3xl p-8 md:p-10 shadow-2xl border border-slate-800 mb-16 relative overflow-hidden" data-id="element-970">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-971" />

          <h3 className="text-2xl font-bold text-cyan-300 mb-4 font-heading" data-id="element-972">
            Life deals your hand.
          </h3>
          <p className="text-slate-300 text-lg leading-relaxed mb-6" data-id="element-973">
            Every player begins with a randomly assigned background, job
            situation, advantages, and setbacks that shape their journey through
            a living, interactive world.
          </p>
          <p className="text-slate-300 text-lg leading-relaxed" data-id="element-974">
            As you progress through the city, your choices, risks, and survival
            instincts determine what opportunities — or disasters — come next.
            Blending strategy, storytelling, survival, and life simulation into
            one unique experience.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20" data-id="element-975">
          {features.map((feature, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.1
        }} className="bg-slate-900/40 backdrop-blur-sm p-6 rounded-2xl border border-slate-800/80 hover:border-cyan-800/50 transition-colors" data-id="element-976">
              <div className="w-12 h-12 rounded-xl bg-cyan-950/50 flex items-center justify-center text-cyan-400 mb-4 border border-cyan-900/50" data-id="element-977">
                {feature.icon}
              </div>
              <h4 className="font-bold text-white text-lg mb-2" data-id="element-978">
                {feature.title}
              </h4>
              <p className="text-slate-400 text-sm leading-relaxed" data-id="element-979">
                {feature.desc}
              </p>
            </motion.div>)}
        </div>

        {/* Waitlist */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center bg-gradient-to-br from-slate-900 to-indigo-950/50 rounded-[3rem] p-10 md:p-16 shadow-2xl border border-indigo-900/30 relative overflow-hidden mb-16" data-id="element-980">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-transparent to-transparent opacity-50" data-id="element-981" />

          <div className="relative z-10" data-id="element-982">
            <h2 className="font-heading text-3xl font-bold text-white mb-4" data-id="element-983">
              Be the first to play.
            </h2>
            <p className="text-indigo-200/70 text-lg mb-10 max-w-md mx-auto" data-id="element-984">
              Join the waitlist to get notified when TTL: The Board Game
              officially launches.
            </p>

            <div className="bg-slate-950/50 backdrop-blur-md p-8 rounded-3xl border border-slate-800 max-w-md mx-auto" data-id="element-985">
              {status === 'success' ? <div className="text-center py-4" data-id="element-986">
                  <p className="font-heading text-2xl font-bold text-cyan-400 mb-2" data-id="element-987">
                    You're on the list!
                  </p>
                  <p className="text-slate-400 font-medium" data-id="element-988">
                    We'll let you know as soon as the game drops.
                  </p>
                </div> : <form className="flex flex-col gap-3" onSubmit={handleSubmit} data-id="element-989">
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" className="w-full px-5 py-4 rounded-xl border border-slate-700 bg-slate-900 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-cyan-500 outline-none" data-id="element-990" />
                  <button type="submit" disabled={status === 'loading'} className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-cyan-600 text-white rounded-xl font-bold hover:bg-cyan-500 transition-colors disabled:opacity-60 shadow-[0_0_15px_rgba(8,145,178,0.4)]" data-id="element-991">
                    {status === 'loading' ? '...' : 'Notify Me'}
                  </button>
                  <p className="text-xs text-slate-500 mt-2 leading-relaxed text-center" data-id="element-992">
                    By subscribing, you agree to our{' '}
                    <Link to="/terms" className="underline hover:text-cyan-300 transition-colors" data-id="element-993">
                      Terms & Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="underline hover:text-cyan-300 transition-colors" data-id="element-994">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </form>}
            </div>
          </div>
        </motion.div>

        {/* Privacy Policy Note */}
        <div className="text-center" data-id="element-995">
          <p className="text-slate-500 text-sm" data-id="element-996">
            Privacy Policy & Terms of Service will be available closer to
            launch. Stay tuned!
          </p>
        </div>
      </div>
    </div>;
}